import type { ColumnDef } from '@tanstack/react-table'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { InfoQuickView } from '@/components/ui/table/components/InfoQuickView.tsx'
import { TooltipWithList } from '@/components/ui/table/components/tooltip-with-list.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { SequenceActionButton } from '@/modules/creator-settings/rebill-messages/components/sequence-action-button.tsx'
import {
  displayTime,
  getDateWithHoursAndMinutesFromTimeObject,
  parseTimeStringToTimObject,
} from '@/modules/creator-settings/rebill-messages/helpers/time.ts'
import type { WelcomeMessage } from '@/modules/creator-settings/types.ts'
import { generateWelcomeTableOptions } from '@/modules/creator-settings/welcome-messages/utils.ts'
import { generateFollowUpsTableOptions } from '@/modules/message/components/follow-ups/utils.ts'

interface Props {
  deleteMessage: (id: number) => Promise<unknown>
  toggleMessage: (id: number, value: boolean) => Promise<unknown>
}

export function buildColumns({
  deleteMessage,
  toggleMessage,
}: Props): ColumnDef<WelcomeMessage>[] {
  return [
    {
      accessorKey: 'message',
      header: 'Message',
      cell: ({ row }) => {
        const followUpMessages = row.original.followUpEnabled
          ? row.original.followUpDto?.sequence?.messages
          : []

        return (
          <TooltipWithList
            messages={row.original.sequence?.messages || []}
            mediaOptions={[]}
            followUpMessages={followUpMessages || []}
          />
        )
      },
    },
    {
      id: 'timeframe',
      header: () => (
        <Tooltip
          side="right"
          sideOffset={20}
          content="Timeframe during which this message will be sent out"
        >
          <div className="flex items-center gap-1">
            Timeframe <InfoIcon className="text-secondary-300 h-4 w-4" />
          </div>
        </Tooltip>
      ),
      cell: ({ row }) => {
        const timeframeStart = row.original.timeframeStart
        const timeframeEnd = row.original.timeframeEnd
        const displayTimeframeStart = timeframeStart
          ? displayTime(
              getDateWithHoursAndMinutesFromTimeObject(
                parseTimeStringToTimObject(timeframeStart),
              ),
            )
          : 'Not Set'
        const displayTimeframeEnd = timeframeEnd
          ? displayTime(
              getDateWithHoursAndMinutesFromTimeObject(
                parseTimeStringToTimObject(timeframeEnd),
              ),
            )
          : 'Not Set'

        return (
          <div className="flex gap-4">
            {displayTimeframeStart}
            {' - '}
            {displayTimeframeEnd}
          </div>
        )
      },
    },
    {
      id: 'actions',
      header: () => <div className="flex w-full justify-end">Actions</div>,
      cell: ({ row }) => {
        const infoQuickViewData = generateWelcomeTableOptions(
          row.original.statistics,
        )
        const infoQuickViewFollowUpData = generateFollowUpsTableOptions(
          row.original.followUpDto?.statistics,
        )

        return (
          <div className="flex w-full justify-end gap-4">
            <InfoQuickView
              data={[infoQuickViewData, infoQuickViewFollowUpData]}
            />
            <SequenceActionButton
              // @ts-ignore
              row={row}
              toggleMessage={toggleMessage}
              deleteMessage={deleteMessage}
            />
          </div>
        )
      },
    },
  ]
}
